/**
 * Turning a temperature into something a person reads.
 *
 * Every temperature in Grow is stored and calculated in Fahrenheit, because
 * that is what the NOAA normals arrive in. Converting happens at the point of
 * display and nowhere else, so nothing downstream has to ask which unit a
 * number is in.
 *
 * Read through utils/temperatureUtils, which re-exports these. The unit
 * words are ui.climateDataBlock.temperatureUnit and temperatureRange.
 *
 * @module temperature/convert
 */

/**
 * Convert Fahrenheit to Celsius
 * @param f Temperature in Fahrenheit
 * @returns Temperature in Celsius, rounded to nearest whole number
 */
const fahrenheitToCelsius = (f: number): number => Math.round((f - 32) * (5/9));

/**
 * Format temperature with unit for display. A missing reading (null,
 * undefined or not a number) is a dash, never 0° or "NaN°".
 * @param tempF Temperature in Fahrenheit, or null when the forecast has none
 * @param unit Desired display unit ('F' | 'C')
 * @returns Formatted temperature string with unit, or '–' when there is no reading
 */
export const formatTemperature = (tempF: number | null | undefined, unit: 'F' | 'C'): string => {
  if (typeof tempF !== 'number' || !Number.isFinite(tempF)) return '–';
  const value = unit === 'C' ? fahrenheitToCelsius(tempF) : tempF;
  return `${Math.round(value)}°${unit}`;
};

/**
 * Format a temperature value with unit using translation keys
 * @param tempF Temperature in Fahrenheit
 * @param unit Desired display unit ('F' | 'C')
 * @param t Translation function
 * @returns Formatted temperature string with unit
 */
export const formatTempWithTranslation = (
  tempF: number, 
  unit: 'F' | 'C',
  t: (key: string, params?: Record<string, string | number>) => string
): string => {
  const value = unit === 'C' ? fahrenheitToCelsius(tempF) : tempF;
  const unitStr = t('ui.climateDataBlock.temperatureUnit', { unit });
  return `${Math.round(value)}${unitStr}`;
};

/**
 * Format a temperature range with units using translation keys
 * @param minF Minimum temperature in Fahrenheit
 * @param maxF Maximum temperature in Fahrenheit
 * @param unit Desired display unit ('F' | 'C')
 * @param t Translation function
 * @returns Formatted temperature range string with units
 */
export const formatTempRangeWithTranslation = (
  minF: number, 
  maxF: number, 
  unit: 'F' | 'C',
  t: (key: string, params?: Record<string, string | number>) => string
): string => {
  const minValue = unit === 'C' ? fahrenheitToCelsius(minF) : minF;
  const maxValue = unit === 'C' ? fahrenheitToCelsius(maxF) : maxF;
  const unitStr = t('ui.climateDataBlock.temperatureUnit', { unit });
  return t('ui.climateDataBlock.temperatureRange', { 
    min: `${Math.round(minValue)}${unitStr}`,
    max: `${Math.round(maxValue)}${unitStr}`
  });
};
